import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Clock3, MapPin, Phone, Mail } from "lucide-react";
import { useSEO, API } from "@/lib/seo";
import { SERVICES, BUDGETS, PHONE_DISPLAY, PHONE_TEL, EMAIL } from "@/data/services";
import { SOCIALS, WHATSAPP_URL } from "@/data/social";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";
import { MaskedLine, FadeIn, Reveal } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const formatError = (detail) => {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
    return String(detail);
};

export default function Contact() {
    useSEO(
        "Get a Free Quote | Digital Circle Calgary",
        "Request a free quote for websites, apps, branding, or marketing in Calgary. Call 403-690-0222 or send the form — we reply within one business day."
    );

    const [params] = useSearchParams();
    const preselect = params.get("service");
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        service: SERVICES.includes(preselect) ? preselect : "",
        budget: "",
        description: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const set = (key) => (e) => setForm({ ...form, [key]: e.target ? e.target.value : e });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.service || !form.budget) {
            toast.error("Please choose a service and a budget range.");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/leads`, form);
            setSubmitted(true);
            toast.success("Thanks — your request is in. We'll reply within one business day.");
        } catch (err) {
            toast.error(formatError(err.response?.data?.detail));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <main className="pt-32 md:pt-44" data-testid="contact-page">
            <section className="mx-auto max-w-7xl px-5 md:px-8">
                <FadeIn>
                    <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.25em] text-slate-500">
                        <span className="ring-logo inline-block h-3.5 w-3.5" />
                        Contact
                    </p>
                </FadeIn>
                <h1 className="mt-6 max-w-4xl text-5xl font-black tracking-tighter text-slate-900 md:text-7xl" data-testid="contact-headline">
                    <MaskedLine delay={0.1}>Tell us what</MaskedLine>
                    <MaskedLine delay={0.22}>
                        <span className="gradient-text">you're building.</span>
                    </MaskedLine>
                </h1>
            </section>

            <section className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-24">
                <div className="grid items-start gap-10 lg:grid-cols-5">
                    {/* Info column */}
                    <div className="space-y-6 lg:col-span-2">
                        <Reveal>
                            <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
                                <h2 className="text-lg font-bold tracking-tight text-slate-900">Prefer to talk?</h2>
                                <a
                                    href={PHONE_TEL}
                                    data-testid="contact-phone-link"
                                    className="group mt-5 flex items-center gap-4 rounded-2xl bg-slate-50 p-4 transition-colors duration-300 hover:bg-slate-100"
                                >
                                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-emerald-500 text-white">
                                        <Phone className="h-5 w-5" />
                                    </span>
                                    <span>
                                        <span className="block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">Call or text</span>
                                        <span className="block text-lg font-extrabold tracking-tight text-slate-900">{PHONE_DISPLAY}</span>
                                    </span>
                                </a>
                                <a
                                    href={WHATSAPP_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="contact-whatsapp-link"
                                    className="mt-3 flex items-center gap-4 rounded-2xl bg-slate-50 p-4 transition-colors duration-300 hover:bg-slate-100"
                                >
                                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#25D366] text-white">
                                        <WhatsAppIcon className="h-5 w-5" />
                                    </span>
                                    <span>
                                        <span className="block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">WhatsApp</span>
                                        <span className="block text-sm font-bold text-slate-900">Chat with us instantly</span>
                                    </span>
                                </a>
                                <a
                                    href={`mailto:${EMAIL}`}
                                    data-testid="contact-email-link"
                                    className="mt-3 flex items-center gap-4 rounded-2xl bg-slate-50 p-4 transition-colors duration-300 hover:bg-slate-100"
                                >
                                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-blue-500 text-white">
                                        <Mail className="h-5 w-5" />
                                    </span>
                                    <span className="min-w-0">
                                        <span className="block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">Email</span>
                                        <span className="block truncate text-sm font-bold text-slate-900">{EMAIL}</span>
                                    </span>
                                </a>
                                <div className="mt-3 flex items-center gap-4 rounded-2xl bg-slate-50 p-4">
                                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-rose-500 text-white">
                                        <MapPin className="h-5 w-5" />
                                    </span>
                                    <span>
                                        <span className="block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">Based in</span>
                                        <span className="block text-sm font-bold text-slate-900">Calgary, Alberta</span>
                                    </span>
                                </div>
                                <div className="mt-6 border-t border-slate-100 pt-5">
                                    <span className="block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">Follow us</span>
                                    <div className="mt-3 flex gap-3">
                                        {SOCIALS.map((s) => (
                                            <a
                                                key={s.label}
                                                href={s.href}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                aria-label={s.label}
                                                data-testid={`contact-social-${s.label.toLowerCase()}`}
                                                className={`flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition-[color,border-color,transform] duration-300 hover:-translate-y-1 ${s.hover}`}
                                            >
                                                <s.icon className="h-4 w-4" />
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <div className="flex items-start gap-4 rounded-3xl border border-slate-200 bg-slate-950 p-8 text-white">
                                <Clock3 className="mt-1 h-6 w-6 shrink-0 text-amber-400" />
                                <div>
                                    <h3 className="font-bold tracking-tight">One business day. That's our promise.</h3>
                                    <p className="mt-2 text-sm leading-relaxed text-slate-400">
                                        Every quote request gets a real reply from a real person — not an autoresponder — within one business day.
                                    </p>
                                </div>
                            </div>
                        </Reveal>
                    </div>

                    {/* Form column */}
                    <Reveal delay={0.1} className="lg:col-span-3">
                        <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm md:p-12">
                            {submitted ? (
                                <div className="py-10 text-center" data-testid="quote-success-message">
                                    <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50">
                                        <CheckCircle2 className="h-9 w-9 text-emerald-500" />
                                    </span>
                                    <h2 className="mt-6 text-3xl font-black tracking-tighter text-slate-900">Request received.</h2>
                                    <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-slate-600 md:text-base">
                                        Thanks, {form.name.split(" ")[0] || "friend"} — we've got your {form.service.toLowerCase()} request. Expect a reply within one business day. Need us sooner? Call{" "}
                                        <a href={PHONE_TEL} className="font-semibold text-slate-900 underline">{PHONE_DISPLAY}</a>.
                                    </p>
                                    <button
                                        onClick={() => { setSubmitted(false); setForm({ name: "", email: "", phone: "", service: "", budget: "", description: "" }); }}
                                        data-testid="quote-another-button"
                                        className="mt-8 rounded-full border border-slate-300 px-6 py-3 text-sm font-semibold text-slate-900 transition-colors duration-300 hover:border-slate-900"
                                    >
                                        Send another request
                                    </button>
                                </div>
                            ) : (
                                <form onSubmit={submit} data-testid="quote-form">
                                    <h2 className="text-2xl font-black tracking-tighter text-slate-900">Get a free quote</h2>
                                    <p className="mt-2 text-sm text-slate-500">No obligation, no pressure — just an honest plan and price.</p>

                                    <div className="mt-8 grid gap-6 sm:grid-cols-2">
                                        <div className="space-y-2">
                                            <Label htmlFor="q-name">Name</Label>
                                            <Input id="q-name" data-testid="quote-name-input" required placeholder="Your full name" value={form.name} onChange={set("name")} className="h-12 rounded-xl" />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="q-email">Email</Label>
                                            <Input id="q-email" data-testid="quote-email-input" required type="email" placeholder="you@company.com" value={form.email} onChange={set("email")} className="h-12 rounded-xl" />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="q-phone">Phone</Label>
                                            <Input id="q-phone" data-testid="quote-phone-input" required type="tel" placeholder="403-555-0100" value={form.phone} onChange={set("phone")} className="h-12 rounded-xl" />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Service needed</Label>
                                            <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                                                <SelectTrigger data-testid="quote-service-select" className="h-12 rounded-xl">
                                                    <SelectValue placeholder="Choose a service" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {SERVICES.map((s) => (
                                                        <SelectItem key={s.slug} value={s.title} data-testid={`quote-service-option-${s.slug}`}>
                                                            {s.title}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="space-y-2 sm:col-span-2">
                                            <Label>Budget range</Label>
                                            <Select value={form.budget} onValueChange={(v) => setForm({ ...form, budget: v })}>
                                                <SelectTrigger data-testid="quote-budget-select" className="h-12 rounded-xl">
                                                    <SelectValue placeholder="Choose a budget range" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {BUDGETS.map((b) => (
                                                        <SelectItem key={b} value={b}>{b}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="space-y-2 sm:col-span-2">
                                            <Label htmlFor="q-desc">Project description</Label>
                                            <Textarea
                                                id="q-desc"
                                                data-testid="quote-description-input"
                                                required
                                                rows={5}
                                                placeholder="What are you building, who is it for, and when do you need it?"
                                                value={form.description}
                                                onChange={set("description")}
                                                className="rounded-xl"
                                            />
                                        </div>
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        data-testid="quote-submit-button"
                                        className="group mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-slate-900 px-8 py-4 text-sm font-semibold text-white shadow-lg transition-[transform,box-shadow,background-color,opacity] duration-300 hover:-translate-y-0.5 hover:bg-slate-800 hover:shadow-xl disabled:opacity-60 sm:w-auto"
                                    >
                                        {submitting ? "Sending…" : "Send my request"}
                                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                                    </button>
                                </form>
                            )}
                        </div>
                    </Reveal>
                </div>
            </section>
        </main>
    );
}
